import React, { useCallback, useContext, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import styles from "./AgendaItemDetails.module.css";
import { AGENDAITEM_PROPERTY, ProtocolTagKeys } from "src/stores/assemblies/AgendaItemStore";
import { CurrentAgendaItemDocuments } from "./documents/CurrentAgendaItemDocuments";
import { AddAgendaDocumentsModal } from "src/components/assemblies/agenda/list/add-documents-modal/AddAgendaDocumentsModal";
import { CustomerType } from "src/components/sidebar/Sidebar";
import RichTextEditor from "src/components/RichTextEditor/RichTextEditor";
import { Editor, EditorEvents } from "@tiptap/react";
import DOMPurify from "dompurify";
import { AgendaVotingProgressModal } from "./progress-modal/AgendaVotingProgressModal";

const AgendaItemDetailsBase = observer(() => {
    const { t } = useTranslation();
    const { agendaitemStore, assemblyStore, navStore, votingStore } = useContext(RootStoreContext);
    const protocolTextRichEditorTextRef = useRef<Editor | null>(null);

    const onAgendaItemProtocolTextRichTextEditorCreated = useCallback((editor: EditorEvents["create"]) => {
        protocolTextRichEditorTextRef.current = editor.editor;
    }, []);

    const onChangeAgendaItemField = useCallback(
        (agendaItemProperty: AGENDAITEM_PROPERTY) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;

            if (agendaitemStore.currentAgendaItem) {
                agendaitemStore.updateAgendaItem(agendaItemProperty, newValue);
            }

            agendaitemStore.setAgendaItemsHaveChanged();
        },
        [agendaitemStore]
    );

    const documentTile = () => {
        if (
            agendaitemStore.currentAgendaItem?.agendaitemfiles &&
            agendaitemStore.currentAgendaItem?.agendaitemfiles.length > 0
        ) {
            return <CurrentAgendaItemDocuments />;
        }
    };

    const onChangeAgendaItemRichTextEditorField = useCallback(
        (agendaItemProperty: AGENDAITEM_PROPERTY) => (update: EditorEvents["update"]) => {
            if (agendaitemStore.currentAgendaItem) {
                if (update.editor.getText().length === 0) {
                    // If the editor content is getting emptied we want to remove the empty paragraph tag
                    agendaitemStore.updateAgendaItem(agendaItemProperty, "");
                } else {
                    agendaitemStore.updateAgendaItem(agendaItemProperty, DOMPurify.sanitize(update.editor.getHTML()));
                }
            }
            agendaitemStore.setAgendaItemsHaveChanged();
        },
        [agendaitemStore]
    );

    const onClickDocumentIcon = useCallback(
        async (e: any) => {
            e.preventDefault();

            assemblyStore.setIsAddingDocumentsDisplayed(true);
        },
        [assemblyStore]
    );

    const onClickOpenVotingModal = useCallback(
        async (e: any) => {
            e.preventDefault();
            await votingStore.loadVotingInformationBasis();
            await votingStore.setIsVotingModalDisplayed(true);
        },
        [votingStore]
    );

    const onClickProtocolTag = useCallback(
        (translationKey: string) => (e: any) => {
            if (agendaitemStore.currentAgendaItem) {
                const textToAdd = agendaitemStore.currentAgendaItem.protocoltext
                    ? ` ${t(translationKey)}`
                    : t(translationKey);
                protocolTextRichEditorTextRef.current
                    ?.chain()
                    .focus()
                    .selectTextblockEnd()
                    .command(({ tr: transcation }) => {
                        transcation.insertText(textToAdd);
                        return true;
                    })
                    .selectTextblockEnd()
                    .run();
            }
        },
        [agendaitemStore.currentAgendaItem, t]
    );

    const protocolTag = () => {
        return Object.values(ProtocolTagKeys).map((translationKey) => (
            <div key={translationKey} className={styles.Tags} onClick={onClickProtocolTag(translationKey)}>
                {t(translationKey)} <UI.Icon icon={UI.SVGIcon.Add} size={12} className={styles.AddIconTags} />
            </div>
        ));
    };

    return (
        <div className={styles.ExpandedContent}>
            <div className={styles.AgendaTitle}>{t("screens.assemblies.agenda.public_declarations")}</div>
            <UI.Input
                className={styles.AgendaInput}
                value={agendaitemStore.currentAgendaItem?.text || ""}
                onChange={onChangeAgendaItemField(AGENDAITEM_PROPERTY.TEXT)}
                label={`${t("screens.assemblies.agenda.title")} *`}
                disabled={agendaitemStore.isAgendaItemLocked}
            />
            <div className={styles.AgendaItemDescription}>
                <UI.Form.Label>{`${t("screens.assemblies.agenda.description")} *`}</UI.Form.Label>
                <RichTextEditor
                    enableContentCheck={true}
                    stickyToolbar={true}
                    content={agendaitemStore.currentAgendaItem?.description || ""}
                    onContentUpdate={onChangeAgendaItemRichTextEditorField(AGENDAITEM_PROPERTY.DESCRIPTION)}
                    disabled={agendaitemStore.isAgendaItemLocked}
                />
            </div>
            <div className={styles.AgendaItemComment}>
                <UI.Form.Label>{t("screens.assemblies.agenda.internalcomments")}</UI.Form.Label>
                <RichTextEditor
                    enableContentCheck={true}
                    stickyToolbar={true}
                    content={agendaitemStore.currentAgendaItem?.internalcomments || ""}
                    placeholder={t("screens.assemblies.agenda.internalcomments_placeholder").toString()}
                    onContentUpdate={onChangeAgendaItemRichTextEditorField(AGENDAITEM_PROPERTY.INTERNALCOMMENTS)}
                    disabled={agendaitemStore.isAgendaItemLocked}
                />
            </div>
            {navStore.customer.type === CustomerType.TEST ? (
                <div className={styles.ProtocolContainer}>
                    <UI.Form.Label>{t("screens.assemblies.agenda.protocoltext")}</UI.Form.Label>
                    {agendaitemStore.isAgendaItemLocked ? undefined : (
                        <div className={agendaitemStore.isAgendaItemLocked ? undefined : styles.ProtocolTags}>
                            {protocolTag()}
                        </div>
                    )}
                    <RichTextEditor
                        enableContentCheck={true}
                        stickyToolbar={true}
                        content={agendaitemStore.currentAgendaItem?.protocoltext || ""}
                        placeholder={
                            agendaitemStore.isAgendaItemLocked
                                ? ""
                                : t("screens.assemblies.agenda.protocol_placeholder").toString()
                        }
                        onContentUpdate={onChangeAgendaItemRichTextEditorField(AGENDAITEM_PROPERTY.PROTOCOLTEXT)}
                        onEditorCreate={onAgendaItemProtocolTextRichTextEditorCreated}
                        disabled={agendaitemStore.isAgendaItemLocked}
                    />
                </div>
            ) : undefined}

            {agendaitemStore.isAgendaItemLocked ? undefined : (
                <>
                    <div className={styles.AddDocument} onClick={onClickDocumentIcon}>
                        <UI.Icon icon={UI.SVGIcon.Add} size={14} className="mr-2" />
                        {t("screens.assemblies.agenda.add_document")}
                    </div>
                    <> {assemblyStore.isAgendaItemTemplate === false ? <AddAgendaDocumentsModal /> : undefined}</>
                </>
            )}
            {documentTile()}
            {navStore.customer.type === CustomerType.TEST ? (
                <div className={styles.Voting} onClick={onClickOpenVotingModal}>
                    <UI.Icon icon={UI.SVGIcon.Add} size={14} className="mr-2" />
                    {t("screens.assemblies.agenda.votings")}
                </div>
            ) : undefined}

            <AgendaVotingProgressModal />
        </div>
    );
});

export const AgendaItemDetails = Sentry.withProfiler(AgendaItemDetailsBase);
